import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  ShieldOutlined,
  StarBorder,
  CheckBoxOutlined,
  PersonOutline,
  ReceiptLongOutlined,
  BrandingWatermarkOutlined,
  NotificationsNoneOutlined,
  SecurityOutlined,
  GavelOutlined,
  MailOutline,
  GraphicEqOutlined,
  StorageOutlined,
  HubOutlined,
  ChevronLeft,
  ChevronRight,
  RestartAlt,
  Close,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import foundationApi from "Services/Foundation";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { apiUrl } from "../../Constants";

const roleCards = [
  { key: "admin", value: "ADMIN", label: "ADMIN", icon: <StarBorder />, color: "#ef476f" },
  { key: "dispatcher", value: "DISPATCHER", label: "DISPATCHER", icon: <CheckBoxOutlined />, color: "#5a4fcf" },
  { key: "account_manager", value: "ACCOUNT_MANAGER", label: "ACCOUNT MANAGER", icon: <PersonOutline />, color: "#7c5cff" },
  { key: "sub_company", value: "SUB_COMPANY", label: "SUB COMPANY", icon: <PersonOutline />, color: "#8f8f8f" },
  { key: "corporate", value: "CORPORATE", label: "CORPORATE", icon: <PersonOutline />, color: "#3b82f6" },
  { key: "hotel", value: "HOTEL", label: "HOTEL", icon: <PersonOutline />, color: "#10b981" },
];
const roleMeta = roleCards.reduce((acc, r) => {
  acc[r.value] = r;
  return acc;
}, {});

const configMenu = [
  // { section: "", items: [
  //   { label: "General", icon: <ReceiptLongOutlined /> },
  //   { label: "Branding", icon: <BrandingWatermarkOutlined /> },
  //   { label: "Notifications", icon: <NotificationsNoneOutlined /> },
  // ]},
  { section: "ACCESS", items: [
    // { label: "Security & SSO", icon: <SecurityOutlined /> },
    { label: "Roles & Permissions", icon: <GavelOutlined />, active: true },
    // { label: "Audit & Compliance", icon: <ReceiptLongOutlined /> },
  ]},
  { section: "INTEGRATIONS", items: [
   /*  { label: "Email / SMTP", icon: <MailOutline /> },
    { label: "API & Webhooks", icon: <GraphicEqOutlined /> },
    { label: "Data & Backup", icon: <StorageOutlined /> },
    { label: "Integrations", icon: <HubOutlined /> }, */
  ]},
];

const Foundation = () => {
  const dispatch = useDispatch();
  const [tab, setTab] = useState("Users & Roles");
  const [activeRole, setActiveRole] = useState("ADMIN");
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [roleCounts, setRoleCounts] = useState({});
  const [roleSlideIndex, setRoleSlideIndex] = useState(0);
  const [foundationRoles, setFoundationRoles] = useState([]);
  const [newRoleModalOpen, setNewRoleModalOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDescription, setNewRoleDescription] = useState("");
  const breadcrumb = useMemo(() => ` > ${tab}`, [tab]);

  const normalizeRole = useCallback((u) => {
    const byType = (u?.user_type || "").toUpperCase();
    if (byType) return byType;
    const byJoin = u?.user_roles_user_roles_user_idTousers?.[0]?.roles?.name;
    return (byJoin || "").toUpperCase();
  }, []);

  const fetchUsers = useCallback(() => {
    dispatch(showLoader("Loading users..."));
    foundationApi.getFoundationUsers().then(
      (res) => {
        dispatch(hideLoader());
        const payload = res?.data?.data?.data || res?.data?.data || res?.data || [];
        setUsers(Array.isArray(payload) ? payload : []);
      },
      (err) => {
        dispatch(hideLoader());
        setUsers([]);
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Failed to fetch users",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [dispatch]);

  const fetchFoundationRoles = useCallback(() => {
    dispatch(showLoader("Loading roles..."));
    foundationApi.getFoundationRoles().then(
      (res) => {
        dispatch(hideLoader());
        const payload = res?.data?.data?.data || res?.data?.data || res?.data || [];
        setFoundationRoles(Array.isArray(payload) ? payload : []);
      },
      (err) => {
        dispatch(hideLoader());
        setFoundationRoles([]);
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Failed to fetch roles",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [dispatch]);

  useEffect(() => {
    if (tab === "Users & Roles") fetchUsers();
    if (tab === "Configuration") fetchFoundationRoles();
  }, [tab, fetchUsers, fetchFoundationRoles]);

  useEffect(() => {
    const counts = {};
    users.forEach((u) => {
      const role = normalizeRole(u);
      if (!role) return;
      counts[role] = (counts[role] || 0) + 1;
    });
    setRoleCounts(counts);
  }, [users, normalizeRole]);

  const allRoles = useMemo(() => {
    const base = roleCards.map((r) => r.value);
    const dynamic = Object.keys(roleCounts).filter((r) => !base.includes(r));
    return [...base, ...dynamic];
  }, [roleCounts]);

  const visibleCount = 6;
  const maxSlide = Math.max(0, allRoles.length - visibleCount);
  const visibleRoles = allRoles.slice(roleSlideIndex, roleSlideIndex + visibleCount);

  useEffect(() => {
    if (!allRoles.includes(activeRole) && allRoles.length) {
      setActiveRole(allRoles[0]);
    }
  }, [allRoles, activeRole]);

  useEffect(() => {
    if (roleSlideIndex > maxSlide) {
      setRoleSlideIndex(maxSlide);
    }
  }, [roleSlideIndex, maxSlide]);

  const handleOpenNewRoleModal = () => {
    setNewRoleName("");
    setNewRoleDescription("");
    setNewRoleModalOpen(true);
  };

  const handleCloseNewRoleModal = () => {
    setNewRoleModalOpen(false);
  };

  const handleCreateRole = () => {
    const payload = {
      name: (newRoleName || "").trim(),
      description: (newRoleDescription || "").trim(),
    };
    if (!payload.name) {
      dispatch(
        showSnackbar({
          message: "Role name is required",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "warning",
        })
      );
      return;
    }
    dispatch(showLoader("Creating role..."));
    foundationApi.createFoundationRole(payload).then(
      (res) => {
        dispatch(hideLoader());
        if (res?.status === 200 || res?.status === 201) {
          dispatch(
            showSnackbar({
              message: res?.data?.message || "Role created successfully",
              autoHideDuration: 2500,
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "success",
            })
          );
          setNewRoleModalOpen(false);
          fetchFoundationRoles();
        }
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Failed to create role",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  };

  const filteredUsers = useMemo(() => {
    const roleScoped = users.filter((u) => normalizeRole(u) === activeRole);
    const q = search.trim().toLowerCase();
    if (!q) return roleScoped;
    return roleScoped.filter((u) => {
      const fullName = `${u?.first_name || ""} ${u?.last_name || ""}`.trim().toLowerCase();
      return (
        fullName.includes(q) ||
        (u?.email || "").toLowerCase().includes(q) ||
        (u?.department || "").toLowerCase().includes(q)
      );
    });
  }, [users, search, activeRole, normalizeRole]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#ececf4" }}>
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #d6d7e6" }}>
        <Box sx={{ px: 2, py: 1.4, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.3 }}>
            <ShieldOutlined sx={{ color: "#dc3d3d", fontSize: 18 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Foundation Module</Typography>
            <Typography sx={{ color: "#a0a0a0", fontSize: 14 }}>{breadcrumb}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" sx={{ textTransform: "none", bgcolor: "#fff" }}>Activity Log</Button>
            <Button variant="outlined" sx={{ textTransform: "none", bgcolor: "#fff" }}>Export</Button>
            <Button variant="contained" sx={{ textTransform: "none", bgcolor: "#f5bf00", color: "#111", "&:hover": { bgcolor: "#e3b000" } }}>
              Add Entity
            </Button>
          </Box>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          textColor="inherit"
          indicatorColor="primary"
          sx={{
            px: 2,
            "& .MuiTab-root": { textTransform: "none", minHeight: 44, color: "#7a7a7a" },
            "& .Mui-selected": { color: "#4a46c8 !important", fontWeight: 600 },
          }}
        >
          <Tab value="Users & Roles" label="Users & Roles" />
          <Tab value="Configuration" label="Configuration" />
        </Tabs>
      </Box>

      {tab === "Users & Roles" && (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography sx={{ fontWeight: 700, color: "#4b4b5a" }}>Roles</Typography>
            <Box sx={{ display: "flex", gap: 0.8 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setRoleSlideIndex((v) => Math.max(0, v - 1))}
                disabled={roleSlideIndex === 0}
                startIcon={<ChevronLeft />}
                sx={{ textTransform: "none" }}
              >
                Prev
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setRoleSlideIndex((v) => Math.min(maxSlide, v + 1))}
                disabled={roleSlideIndex >= maxSlide}
                endIcon={<ChevronRight />}
                sx={{ textTransform: "none" }}
              >
                Next
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setRoleSlideIndex(0)}
                disabled={roleSlideIndex === 0}
                startIcon={<RestartAlt />}
                sx={{ textTransform: "none" }}
              >
                Start
              </Button>
            </Box>
          </Box>
          <Grid container spacing={2}>
            {visibleRoles.map((roleValue) => {
              const card = roleMeta[roleValue] || {
                key: roleValue.toLowerCase(),
                value: roleValue,
                label: roleValue.replaceAll("_", " "),
                icon: <PersonOutline />,
                color: "#6b7280",
              };
              return (
              <Grid item xs={12} md={4} lg={2} key={card.key}>
                <Card
                  onClick={() => setActiveRole(card.value)}
                  sx={{
                    borderRadius: 2.2,
                    border: activeRole === card.value ? "1px solid #4a46c8" : "1px solid #d8d9e7",
                    boxShadow: "none",
                    height: "100%",
                    cursor: "pointer",
                  }}
                >
                  <CardContent sx={{ p: 2.8, minHeight: 168, display: "flex", flexDirection: "column" }}>
                    <Box sx={{ width: 46, height: 46, borderRadius: 2, bgcolor: "#f3f2f8", display: "grid", placeItems: "center", mb: 1.8 }}>
                      {React.cloneElement(card.icon, { sx: { color: card.color, fontSize: 24 } })}
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 15.5, mb: 1 }}>{card.label}</Typography>
                    <Typography sx={{ color: "#6f6f6f", mb: 1.8 }}>{roleCounts[card.value] || 0} members</Typography>
                    <Box sx={{ mt: "auto", height: 5, borderRadius: 3, bgcolor: "#d8d4e8" }} />
                  </CardContent>
                </Card>
              </Grid>
            )})}
          </Grid>

          <Card sx={{ mt: 2, borderRadius: 2.2, border: "1px solid #d8d9e7", boxShadow: "none" }}>
            <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Team Members</Typography>
              <Typography sx={{ color: "#6f6f6f" }}>{filteredUsers.length} users</Typography>
              <TextField size="small" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} sx={{ width: 220, ml: 1 }} />
              <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                <Button variant="outlined" sx={{ textTransform: "none", bgcolor: "#fff" }}>Import CSV</Button>
                <Button variant="contained" sx={{ textTransform: "none", bgcolor: "#574cc6", color: "#fff", "&:hover": { bgcolor: "#4e43b3" } }}>
                  + Invite User
                </Button>
              </Box>
            </Box>
            <Divider />
            <Box sx={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1.4fr 1.4fr 1fr 1.4fr", px: 1.8, py: 1.4, color: "#7d7d7d", fontWeight: 600 }}>
              <Typography>User</Typography><Typography>Role</Typography><Typography>Department</Typography><Typography>Last Active</Typography><Typography>Status</Typography><Typography>Actions</Typography>
            </Box>
            <Divider />
            {filteredUsers.length === 0 ? (
              <Box sx={{ py: 6, textAlign: "center", color: "#9c9c9c", fontSize: 14 }}>No users found.</Box>
            ) : (
              filteredUsers.map((u, idx) => (
                <Box
                  key={u?.id || idx}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1.4fr 1fr 1.4fr 1.4fr 1fr 1.4fr",
                    px: 1.8,
                    py: 1.2,
                    borderTop: "1px solid #ececf4",
                    alignItems: "center",
                  }}
                >
                  {/* USER + AVATAR */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    <Avatar
                      src={`${apiUrl}${u?.avatar_url}` }
                      alt={u?.first_name}
                      sx={{
                        width: 38,
                        height: 38,
                        bgcolor: "#16a34a",
                        fontSize: 14,
                        fontWeight: 700,
                      }}
                    >
                      {u?.first_name?.[0] || u?.name?.[0] || "U"}
                    </Avatar>
              
                    <Typography sx={{ fontWeight: 500 }}>
                      {`${u?.first_name || ""} ${u?.last_name || ""}`.trim() ||
                        u?.name ||
                        "--"}
                    </Typography>
                  </Box>
              
                  <Typography>{normalizeRole(u) || "--"}</Typography>
              
                  <Typography>{u?.department || "--"}</Typography>
              
                  <Typography>
                    {u?.updated_at
                      ? new Date(u.updated_at).toLocaleDateString()
                      : "--"}
                  </Typography>
              
                  <Typography
                    sx={{
                      color: u?.is_active ? "#16a34a" : "#dc2626",
                      fontWeight: 600,
                    }}
                  >
                    {u?.is_active ? "Active" : "Inactive"}
                  </Typography>
              
                  <Typography sx={{ color: "#7d7d7d" }}>--</Typography>
                </Box>
              ))
            )}
          </Card>
        </Box>
      )}

      {tab === "Configuration" && (
        <Box sx={{ display: "flex", minHeight: "calc(100vh - 140px)" }}>
          <Box sx={{ width: 270, bgcolor: "#fff", borderRight: "1px solid #d8d9e7", p: 2, overflowY: "auto" }}>
            {configMenu.map((group, idx) => (
              <Box key={idx} sx={{ mb: 2.5 }}>
                {group.section ? <Typography sx={{ fontSize: 12, color: "#8e8e8e", letterSpacing: 1, mb: 1 }}>{group.section}</Typography> : null}
                {group.items.map((item) => (
                  <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 0.2, py: 1.1, color: item.active ? "#e01818" : "#233044", fontWeight: item.active ? 700 : 600 }}>
                    {React.cloneElement(item.icon, { sx: { fontSize: 22 } })}
                    <Typography>{item.label}</Typography>
                  </Box>
                ))}
                <Divider sx={{ mt: 1 }} />
              </Box>
            ))}
          </Box>

          <Box sx={{ flex: 1, p: 2 }}>
            <Card sx={{ borderRadius: 2.2, border: "1px solid #d8d9e7", boxShadow: "none" }}>
              <Box sx={{ p: 2.2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 0.4 }}>Roles & Permissions</Typography>
                  <Typography sx={{ color: "#6f6f6f" }}>Define what each role can see and do</Typography>
                </Box>
                <Button variant="contained" onClick={handleOpenNewRoleModal} sx={{ textTransform: "none", bgcolor: "#5a4fcf", "&:hover": { bgcolor: "#4e44bb" } }}>
                  + New Role
                </Button>
              </Box>
            </Card>

            <Card sx={{ mt: 1.5, borderRadius: 2.2, border: "1px solid #d8d9e7", boxShadow: "none" }}>
              {foundationRoles.length === 0 ? (
                <Box sx={{ p: 3, color: "#8f8f9f" }}>No roles found.</Box>
              ) : (
                foundationRoles.map((role, idx) => {
                  const roleName = role?.name || role?.role_name || "--";
                  const roleDescription = role?.description || role?.desc || "";
                  const normalizeRolePermissions = () => {
                    const raw = role?.role_permissions ?? role?.rolePermissions ?? [];
                    if (Array.isArray(raw)) return raw;
                    if (typeof raw === "string") {
                      try {
                        const parsed = JSON.parse(raw);
                        return Array.isArray(parsed) ? parsed : [];
                      } catch {
                        return [];
                      }
                    }
                    return [];
                  };
                  const rolePermissions = normalizeRolePermissions();
                  const fromRolePermissions = rolePermissions
                    .map((p) => p?.permissions?.module || p?.permission?.module || p?.module)
                    .filter(Boolean);
                  const fromPermissions = (role?.permissions || [])
                    .map((p) => (typeof p === "string" ? p : p?.name))
                    .filter(Boolean);
                  const permissions = Array.from(
                    new Set(
                      (fromRolePermissions.length > 0 ? fromRolePermissions : fromPermissions)
                        .map((v) => String(v).trim())
                        .filter(Boolean)
                    )
                  );
                  const permsCount =
                    role?.permissions_count ||
                    rolePermissions.length ||
                    permissions.length;
                  const isSystem = !!role?.is_system;

                  return (
                    <Box key={role?.id || idx} sx={{ p: 2.2, borderTop: idx === 0 ? "none" : "1px solid #ececf4" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                        <Box
                          sx={{
                            px: 1.2,
                            py: 0.5,
                            borderRadius: 1.6,
                            bgcolor: "#eaf0ff",
                            color: "#234dce",
                            fontWeight: 700,
                            fontSize: 26 / 2,
                          }}
                        >
                          {roleName}
                        </Box>
                        {isSystem ? (
                          <Box
                            sx={{
                              px: 1.1,
                              py: 0.35,
                              borderRadius: 1.2,
                              bgcolor: "#efeff4",
                              color: "#7e7f8a",
                              fontSize: 22 / 2,
                            }}
                          >
                            system
                          </Box>
                        ) : null}
                        <Typography sx={{ color: "#8f8f9f" }}>{roleDescription}</Typography>
                        <Typography sx={{ ml: "auto", color: "#8f8f9f" }}>{permsCount} perms</Typography>
                        <Button variant="outlined" sx={{ textTransform: "none", minWidth: 58 }}>Edit</Button>
                      </Box>

                      <Box sx={{ mt: 1.1, display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                        {permissions.length === 0 ? (
                          <Typography sx={{ color: "#9ca0b0", fontSize: 12 }}>No permissions</Typography>
                        ) : permissions.map((perm, pIdx) => (
                          <Box
                            key={`${role?.id || idx}-${perm}-${pIdx}`}
                            sx={{
                              px: 1.1,
                              py: 0.45,
                              borderRadius: 1.1,
                              bgcolor: "#f1f1f6",
                              color: "#53545f",
                              fontSize: 24 / 2,
                              lineHeight: 1.2,
                            }}
                          >
                            {perm}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })
              )}
            </Card>
          </Box>
        </Box>
      )}

      <Dialog open={newRoleModalOpen} onClose={handleCloseNewRoleModal} fullWidth maxWidth="md">
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 2.5, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 28 / 2 }}>New Role</Typography>
              <Typography sx={{ color: "#6b7280", fontSize: 12 }}>Define name and permissions</Typography>
            </Box>
            <IconButton onClick={handleCloseNewRoleModal} size="small"><Close /></IconButton>
          </Box>
          <Box sx={{ p: 2.5 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: 12, color: "#4b5563", mb: 0.6 }}>Role name</Typography>
                <TextField fullWidth size="small" placeholder="e.g. Contract Manager" value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: 12, color: "#4b5563", mb: 0.6 }}>Description</Typography>
                <TextField fullWidth size="small" placeholder="Short description..." value={newRoleDescription} onChange={(e) => setNewRoleDescription(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 12, color: "#4b5563", mb: 0.8 }}>Permissions</Typography>
                <Box sx={{ border: "1px solid #d1d5db", borderRadius: 2, minHeight: 58, display: "grid", placeItems: "center", color: "#9ca3af", fontSize: 14 }}>
                  No permissions available
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ px: 2.5, py: 1.8, borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: 12, color: "#6b7280" }}>0 permissions selected</Typography>
            <Box sx={{ display: "flex", gap: 1.2 }}>
              <Button variant="outlined" onClick={handleCloseNewRoleModal} sx={{ textTransform: "none" }}>Cancel</Button>
              <Button variant="contained" onClick={handleCreateRole} sx={{ textTransform: "none", bgcolor: "#e11d48", "&:hover": { bgcolor: "#be123c" } }}>
                Create Role
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Foundation;
